import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Switch, Route, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Popup, Table, TableFilter, ViewDownloadStatus } from '../../components';
// Constants
import { ENTITY_TYPE } from '../../utils/constants/commonValues';
// Actions
import {
    fetchTIDRequest,
    fetchEntityListRequest,
    terminalIDBulkUploadRequest,
    generateTerminalIdReportRequest,
    generateTerminalIdReportError,
    getTerminalOnboardingActiveReportRequest,
    downloadTerminalOnboardingReportRequest,
    getTerminalOnboardingActiveReportError,
    downloadTerminalOnboardingReportError,
    terminalIdBulkFileUploadRequest
} from './redux/TIDOnBoard.actions';
// Local Components
import CreateTerminal from './CreateTerminal';
import EditTerminal from './EditTerminal';
import ViewTerminal from './ViewTerminal';
import UserActionsMob from './UserActionsMob';
// Styles
import './TerminalOnboard.scss';
import BulkUpload from './models/BulkUpload/BulkUploadModal';
import BulkCreationModal from './models/BulkCreation/BulkCreationModal';
import { filterFieldList } from './constant';
import DownloadFile from './DownloadFile';
import BulkTerminalIdUpload from './BulkTerminalIdUpload';

const TerminalOnboard = props => {
    const {
        auth,
        history,
        actions,
        loader: { showLoader },
        terminalsData,
    } = props;

    const {
        error,
        tableData,
        isLoading,
        pagination,
        entityList,
        loading
    } = terminalsData;

    const pageSizeOptions = [10, 20, 40, 60];
    const [pageCount, setPageCount] = useState(0);
    const [roleStatus, setRoleStatus] = useState('');
    const [filterData, setFilterData] = useState({});
    const [isResetPage, setIsResetPage] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [filterList, setFilterList] = useState([]);
    const [bulkIsOpen, setBulkIsOpen] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const { activeReports, generatedFile } = terminalsData || {};
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const hasBulKUpdateEditPermission = auth?.resourceDetails?.permissions?.edit_merchant_onboarding?.includes('edit_tid');
    const hasBulkUpdateCreatePermission = auth?.resourceDetails?.permissions?.merchant_onboarding?.includes('create_merchant');
    const handleCreateMerchant = () => {
        history.push('/tid-onboarding/create/terminal');
    };

    const handleBulkUpload = () => {
        history.push('/tid-onboarding/bulk-terminal-id-upload');
    }

    const columns = useMemo(
        () => [
            { header: 'Terminal Name', accessor: 'name' },
            { header: 'Terminal ID ', accessor: 'entityCode' },
            { header: 'Merchant Name', accessor: 'parentEntityName' },
            { header: 'Created Date', accessor: 'createdDate' },
            { header: 'Status', accessor: 'status' },
            { header: 'Action', accessor: 'action' },
        ],
        []
    );

    const fetchData = useCallback(
        ({ pageSize, pageIndex, roleStatus, appliedFilters }) => {
            const data = { pageSize, pageIndex, roleStatus, sort: 'name' };
            actions.fetchTIDRequest({ ...data, ...appliedFilters, ...filterData });
            setPageSize(pageSize);
        },
        [actions, filterData]
    );

    const handleBulkUploadClose = () => {
        setBulkIsOpen(false);
    };

    const handleViewDownload = () => {
        setShowFilter(true);       
    };
    
    const handleCloseFilterViewDownloads = () => {
        setShowFilter(false);
    }

    useEffect(() => {
        if (generatedFile.isSuccess) {
            setShowFilter(true);
            setShowDownloadPopup(false);
        }
        return () => {
            actions.generateTerminalIdReportError({ error: {} });
        };
    }, [actions, generatedFile.isSuccess]);

    const [bulkCreationIsOpen, setBulkCreationIsOpen] = useState(false);

    const handleBulkCreationClose = () => {
        setBulkCreationIsOpen(false);
    };

    const handleSearchEnd = (value, entityType) => {
        const filtered =
            entityList.merchant?.filter(item => {
                const key = 'name';
                return item[key]?.toLowerCase().includes(value.toLowerCase());
            }) || [];

        const data = {
            entityName: value,
            entityCode: '',
        };

        if (filtered.length === 0 && value.length >= 3) {
            actions.fetchEntityListRequest(entityType, data);
        }
    };

    useEffect(() => {
        actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT);
    }, [actions]);

    useEffect(() => {
        if (entityList.merchant) {
            const data = entityList.merchant;
            const newFilter = filterFieldList.map(field => {
                if (
                    field.name === 'merchantId' ||
                    field.name === 'merchantName'
                ) {
                    field.props.data = data;
                    field.props.handleSearchEnd = handleSearchEnd;
                }
                return field;
            });
            setFilterList(newFilter);
        }
    }, [entityList]);

    useEffect(() => {
        if (pagination?.totalNumberOfRecords) {
            setPageCount(Math.ceil(pagination.totalNumberOfRecords / pageSize));
        }
    }, [pageSize, pagination.totalNumberOfRecords]);

    return (
        <div className="mob-wrapper">
            <BulkUpload
                isOpen={bulkIsOpen}
                handleClose={handleBulkUploadClose}
                actions={actions}
            />
            <BulkCreationModal
                isOpen={bulkCreationIsOpen}
                handleCloseModal={handleBulkCreationClose}
            />
            <Switch>
                <Route exact path="/tid-onboarding">
                    <Table
                        id="tid-edit"
                        tableTitle="Terminal ID Onboarding"
                        filterActionTitle="Create Terminal ID"
                        // filterActionPermission="create_merchant_button"
                        filterActionPermission="create_merchant"
                        columns={columns}
                        isFiltersEnable
                        isPaginationEnable={true}
                        data={tableData || []}
                        filterData={filterData}
                        error={error && error.errorMessage}
                        loading={showLoader || isLoading || loading}
                        roleStatus={roleStatus}
                        pageCount={pageCount}
                        fetchData={fetchData}
                        numberOfRecord={
                            pagination && pagination.totalNumberOfRecords
                        }
                        pageSizeOptions={pageSizeOptions}
                        paginationLabel="Rows per page"
                        onClickTableAction={handleCreateMerchant}
                        setRoleStatus={setRoleStatus}
                        isResetPage={isResetPage}
                        afterResetPage={() => setIsResetPage(false)}
                        userActions={data => (
                            <UserActionsMob
                                data={data}
                                auth={auth}
                            />
                        )}
                        pagePerView={3}
                        additionalActions={props => (
                            <div className="button-container" style={{ display: 'flex', gap: '10px' }}>
                                {(
                                    <button
                                        className="custom-btn"
                                        id="bulk-upload-btn"
                                        type="button"
                                        onClick={handleBulkUpload}
                                    >
                                        BULK UPLOAD
                                    </button>
                                )}

                                {(hasBulKUpdateEditPermission || hasBulkUpdateCreatePermission) && (
                                    <button
                                        className="custom-btn"
                                        id="bulk-update-btn"
                                        type="button"
                                        onClick={() => setBulkIsOpen(true)}
                                    >
                                        BULK UPDATE
                                    </button>
                                )}

                                <div className="download-container" style={{ display: 'flex'}}  >
                                    <DownloadFile
                                        filterData={filterData}
                                        tableData={tableData}
                                        actions={actions}
                                        showDownloadPopup={showDownloadPopup}
                                        setShowDownloadPopup={setShowDownloadPopup}
                                        generateData={generatedFile}
                                    />
                                    <button
                                            className="custom-btn"
                                            id="view-download-btn"
                                            type="button"
                                            onClick={() => handleViewDownload(true)}
                                        >
                                            View Downloads
                                    </button>
                                </div>
                            </div>
                        )}
                        tableFilters={
                            <TableFilter
                                showOverlay
                                isResetFilterOnLoad
                                fieldsList={filterList}
                                onSubmit={filterValues => {
                                    setFilterData(filterValues);
                                    setIsResetPage(true);
                                }}
                                onReset={resetValue => {
                                    setFilterData(resetValue);
                                    setIsResetPage(true);
                                }}
                            />
                        }
                        tableActionPopup={
                            <>
                                    <Popup
                                        title="Download Status"
                                        showCloseIcon
                                        showOverlay
                                        showFilter={showFilter}
                                        closePopup={handleCloseFilterViewDownloads}
                                    >
                                        <ViewDownloadStatus pageType={'TerminalOnboarding'} reportType={'TID_ONBOARDING'} actions={actions} data={activeReports?.reports || ''} />
                                    </Popup>
                            </>
                        }
                    />
                </Route>
                <Route path="/tid-onboarding/create/:boardingType">
                    <CreateTerminal history={history} />
                </Route>
                <Route path="/tid-onboarding/edit/:boardingType/:terminalId">
                    <EditTerminal />
                </Route>
                <Route path="/tid-onboarding/view/:boardingType/:terminalId">
                    <ViewTerminal />
                </Route>
                <Route path="/tid-onboarding/bulk-terminal-id-upload">
                    <BulkTerminalIdUpload history={history}/>
                </Route>
            </Switch>
        </div>
    );
};

const mapStateToProps = ({ form, loader, editData, terminalsData, auth }) => ({
    form,
    loader,
    editData,
    terminalsData,
    auth
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchTIDRequest,
            fetchEntityListRequest,
            terminalIDBulkUploadRequest,
            generateTerminalIdReportRequest,
            getTerminalOnboardingActiveReportRequest,
            downloadTerminalOnboardingReportRequest,
            generateTerminalIdReportError,
            getTerminalOnboardingActiveReportError,
            downloadTerminalOnboardingReportError,
            terminalIdBulkFileUploadRequest
        },
        dispatch
    ),
});

TerminalOnboard.propTypes = {
    actions: PropTypes.object,
    history: PropTypes.object,
    tidData: PropTypes.object,
    loader: PropTypes.object,
    terminalsData: PropTypes.object,
    auth :PropTypes.object
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(TerminalOnboard)
);
